// --------------------------------------------------------------
// Created On: 2021-09-21
// Author: Zachary Thomas
//
// Last Modified: 2026-05-06
// Modified By: Jonathon Hicke
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, useEffect, Fragment } from "react";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import SaveChangesModal from "../../../components/SaveChangesModal/SaveChangesModal";
import getApiError from "../../../utilities/api/getApiError";
import Modal from "../../../components/Modal/Modal";
import ModalHeader from "../../../components/ModalHeader/ModalHeader";
import ModalBody from "../../../components/ModalBody/ModalBody";
import ModalFooter from "../../../components/ModalFooter/ModalFooter";
import Error from "../../../components/Error/Error";
import Spinner from "../../../components/Spinner/Spinner";
import apiRequest from "../../../utilities/api/apiRequest";
import { API, MIN_FULL_NAME_LENGTH, MAX_FULL_NAME_LENGTH } from "../../../constants/miscellaneous";
import useReduxSelector from "src/hooks/useReduxSelector";
import { USER_TYPES } from "../../../constants/reducerActions";
import userHasPermission from "../../../utilities/userHasPermission";
import PhoneNumberInput from "../../../components/PhoneNumberInput/PhoneNumberInput";
import { DELETE_USERS_PERMISSION, UPDATE_USERS_PERMISSION } from "../../../constants/permissions";
import styles from "./UserModal.module.scss";
import useApi from "src/hooks/useApi";
import GetUserResponseBody from "src/types/api/GetUserResponseBody";
import IconTooltip from "src/components/IconTooltip/IconTooltip";

// Modal for editing, and deleting users.
export default function UserModal(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showConfirmExit, setShowConfirmExit] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [callingCode, setCallingCode] = useState<string>("1");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [roleId, setRoleId] = useState<number>(0);
  const [requiresMfa, setRequiresMfa] = useState<boolean>(false);
  const [mfaExempt, setMfaExempt] = useState<boolean>(false);

  const currentUser = useReduxSelector((state) => state.currentUser);

  // Get user each time we open the Modal in case we make edits without saving.
  useApi(
    () => {
      if (props.userId !== undefined) {
        setLoading(true);
        return true;
      } else {
        return false;
      }
    },
    { method: "GET", url: `${API}/company/${currentUser.companyId}/user/${props.userId}` },
    async (response: Response, responseBody: GetUserResponseBody) => {
      if (response.ok && responseBody) {
        setPhoneNumber(responseBody.phoneNumber || "");
        setCallingCode(responseBody.phoneNumberCountryCode || "1");
        setName(props.name);
        setRoleId(props.roleId);
        setRequiresMfa(responseBody.requiresMfa);
        setMfaExempt(responseBody.mfaExempt);
      }
      setLoading(false);
    },
    [props.showModal]
  );

  // Update state when user prop changes. We can immediately populate the initial state of the modal without having to
  // wait on the GET /user/{userId} api call.
  useEffect(() => {
    if (props.showModal) {
      setName(props.name);
      setRoleId(props.roleId);
      if (
        props.phoneNumberCountryCode !== null &&
        props.phoneNumberCountryCode !== undefined &&
        props.phoneNumber !== null &&
        props.phoneNumber !== undefined
      ) {
        setCallingCode(props.phoneNumberCountryCode);
        setPhoneNumber(props.phoneNumber);
      } else {
        setCallingCode("1");
        setPhoneNumber("");
      }
    }
  }, [props.showModal, props.name, props.roleId, props.phoneNumberCountryCode, props.phoneNumber]);

  // Validate the user settings.
  function userIsValid(): boolean {
    if (name.trim().length < MIN_FULL_NAME_LENGTH || name.trim().length > MAX_FULL_NAME_LENGTH) {
      setErrorMessage(
        `A user's name must be between ${MIN_FULL_NAME_LENGTH} and ${MAX_FULL_NAME_LENGTH} characters long.`
      );
      return false;
    } else {
      return true;
    }
  }

  // Edit a user.
  async function editUser(): Promise<void> {
    if (userIsValid()) {
      let requestPhoneNumber: string | null = null;
      let requestCallingCode: string | null = null;

      if (
        phoneNumber !== null &&
        phoneNumber !== undefined &&
        callingCode !== undefined &&
        callingCode !== null &&
        phoneNumber.length > 5
      ) {
        requestPhoneNumber = phoneNumber;
        requestCallingCode = callingCode;
      }

      const requestBody = {
        name: name.trim(),
        userState: "ENABLED",
        phoneNumberCountryCode: requestCallingCode,
        phoneNumber: requestPhoneNumber,
        roleId: roleId,
        mfaExempt: mfaExempt,
      };

      setLoading(true);
      const [response] = (await apiRequest(
        `${API}/company/${currentUser.companyId}/user/${props.userId}`,
        "PUT",
        requestBody
      )) as [Response, PutResponseBody];
      setLoading(false);

      if (response.ok) {
        const user = {
          userId: props.userId,
          emailAddress: props.emailAddress,
          name: name,
          phoneNumberCountryCode: requestCallingCode,
          phoneNumber: requestPhoneNumber,
          roleId: roleId,
          mfaExempt: mfaExempt,
        };

        setErrorMessage("");
        props.onClose();

        props.onAction({
          type: USER_TYPES.UPDATE_USER,
          payload: {
            user: user,
          },
        });
      } else {
        setErrorMessage(await getApiError(response, "Unable to update user."));
      }
    }
  }

  // Delete a user.
  async function deleteUser(userId: number): Promise<void> {
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/user/${props.userId}`,
      "DELETE",
      null
    )) as [Response, DeleteResponseBody];
    setLoading(false);

    if (response.ok) {
      discardChanges();
      props.onAction({
        type: USER_TYPES.DELETE_USER,
        payload: {
          userId: userId,
        },
      });
    } else {
      setShowConfirmDelete(false);
      setErrorMessage(await getApiError(response, "Unable to delete user."));
    }
  }

  // Exit modal if no changes have been made. Otherwise prompt user.
  function exitModal(): void {
    let previousCallingCode = "1";
    if (props.phoneNumberCountryCode !== null && props.phoneNumberCountryCode !== undefined) {
      previousCallingCode = props.phoneNumberCountryCode;
    }

    let previousPhoneNumber = "";
    if (props.phoneNumber !== null && props.phoneNumber !== undefined) {
      previousPhoneNumber = props.phoneNumber;
    }

    if (
      name === props.name &&
      roleId === props.roleId &&
      previousCallingCode === callingCode &&
      previousPhoneNumber === phoneNumber
    ) {
      // Since there have been no changes we can safely exit.
      discardChanges();
    } else {
      // We have unsaved changes, give the user a chance to save them.
      setShowConfirmExit(true);
    }
  }

  // Save changes.
  function saveChanges(): void {
    void editUser();
    setShowConfirmExit(false);
  }

  // Exit without saving changes.
  function discardChanges(): void {
    setShowConfirmExit(false);
    setShowConfirmDelete(false);
    setErrorMessage("");
    setCallingCode("1");
    setPhoneNumber("");
    props.onClose();
  }

  return (
    <div className="user-modal-container">
      <Spinner loading={loading} />

      <Modal
        show={props.showModal}
        onHide={() => exitModal()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--standard-modal-z-index)" }}
        size="lg"
        animation
      >
        <ModalHeader>
          <h5 className="font-weight-bold">
            <span>Edit User</span>
          </h5>
        </ModalHeader>

        <ModalBody>
          {!loading && (
            <Fragment>
              <div className={`${styles.emailAddress} mx-2 mb-2`}>{props.emailAddress}</div>

              <div className="mx-2 mt-3">
                <label className="mb-3">Name</label>
                <input
                  data-test="user-modal-name-input"
                  className="form-control mx-auto"
                  type="text"
                  maxLength={MAX_FULL_NAME_LENGTH}
                  value={name}
                  disabled={!userHasPermission([[UPDATE_USERS_PERMISSION]])}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="mx-2 mt-3">
                <PhoneNumberInput
                  data-test="user-modal-phone-number-input"
                  labelText="Phone Number"
                  labelClassName="mb-3"
                  phoneNumber={phoneNumber}
                  callingCode={callingCode}
                  onChangePhoneNumber={(phoneNumber) => setPhoneNumber(phoneNumber)}
                  onChangeCallingCode={(callingCode) => setCallingCode(callingCode)}
                />
              </div>

              <div className="mx-2 mt-3">
                <label className="mb-3">Role</label>
                <select
                  data-test="user-modal-role-select"
                  className="form-select"
                  value={roleId}
                  disabled={!userHasPermission([[UPDATE_USERS_PERMISSION]])}
                  onChange={(e) => setRoleId(parseInt(e.target.value, 10))}
                >
                  {props.roles.map((role) => (
                    <option key={role.roleId} value={role.roleId}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              {requiresMfa && (
                <div className="mx-2 mt-3">
                  <label className="mb-3">Exclude from MFA policy</label>
                  <IconTooltip
                    id="user-mfa-required-tooltip"
                    icon="info-circle"
                    message="If checked, this user will not be required to set up MFA, even if it’s enforced company-wide."
                    color="var(--info-tooltip)"
                  />
                  <input
                    className="form-check-input mx-2"
                    data-test="mfa-exempt-checkbox"
                    type="checkbox"
                    id="mfaExempt"
                    checked={mfaExempt}
                    onChange={() => setMfaExempt((prev) => !prev)}
                  />
                </div>
              )}

              {errorMessage.length > 0 && (
                <div className="row">
                  <div className="col mt-4 mx-2">
                    <Error message={errorMessage} />
                  </div>
                </div>
              )}
            </Fragment>
          )}
        </ModalBody>

        <ModalFooter>
          <Fragment>
            {userHasPermission([[DELETE_USERS_PERMISSION]]) && (
              <button
                data-test="user-modal-delete-button"
                className={`${styles.button} btn btn-danger me-auto`}
                type="button"
                onClick={() => setShowConfirmDelete(true)}
              >
                Delete
              </button>
            )}

            {userHasPermission([[UPDATE_USERS_PERMISSION]]) && (
              <button
                data-test="user-modal-save-changes-button"
                className={`${styles.button} btn btn-primary`}
                type="button"
                onClick={() => editUser()}
              >
                Save Changes
              </button>
            )}

            <button
              data-test="user-modal-cancel-button"
              className={`${styles.button} btn btn-secondary`}
              type="button"
              onClick={() => exitModal()}
            >
              Cancel
            </button>
          </Fragment>
        </ModalFooter>
      </Modal>

      <ConfirmModal
        showModal={props.showModal && showConfirmDelete}
        title={`Delete '${name}'?`}
        content={`Are you sure that you want to delete the user '${name}'?`}
        yesText="Delete User"
        noText="Cancel"
        danger={true}
        onClose={() => setShowConfirmDelete(false)}
        onYes={() => deleteUser(props.userId)}
        onNo={() => setShowConfirmDelete(false)}
      />

      <SaveChangesModal
        showModal={props.showModal && showConfirmExit}
        title="Changes have not been saved!"
        content="Are you sure that you want to exit without saving your changes?"
        onClose={() => setShowConfirmExit(false)}
        onSave={() => saveChanges()}
        onNoSave={() => discardChanges()}
      />
    </div>
  );
}

interface Props {
  showModal: boolean;
  userId: number;
  name: string;
  emailAddress: string;
  phoneNumberCountryCode: string | null;
  phoneNumber: string | null;
  roleId: number;
  roles: Role[];
  onClose: () => void;
  onAction: (action: Action) => void;
}

interface Role {
  roleId: number;
  name: string;
  description: string;
  immutable: boolean;
}

interface PutResponseBody {
  message: string;
}

interface DeleteResponseBody {
  message: string;
}

interface User {
  userId: number;
  name: string;
  emailAddress: string;
  phoneNumberCountryCode: string | null;
  phoneNumber: string | null;
  roleId: number;
  mfaExempt: boolean;
}

interface Action {
  type: string;
  payload: Payload;
}

interface Payload {
  user?: User;
  userId?: number;
}
